import { Big } from 'big.js';
import {
    AbstractDeviceConsumablePricingItem,
    AbstractDevicePricingItem,
    Article,
    Corridor,
    ConsumablePricingItem,
    DeviceConsumablePricingItem,
    DeviceConsumableWithSuppPricingItem,
    DevicePricingItem,
    EnumQuoteServiceSalesType,
    EnumQuoteServiceType,
    MathUtils,
    OptionPricingItem,
    PackPricingItem,
    PricingItem,
    Tools,
    WEEK_AVERAGE,
    bigToString,
    parseBig
} from 'quote-domain-core';
import { ArticleService } from '../article/article-service';
import { CorridorService } from '../corridor/corridor-service';
import { singleton } from 'tsyringe';

@singleton()
export class PricingItemCorridorService {
    constructor(
        private readonly articleService: ArticleService,
        private readonly corridorService: CorridorService
    ) {
    }

    public searchAndApplyBaseCorridor(pricingItem: PricingItem, contractInfoInputs: any, code: string, salesType: EnumQuoteServiceSalesType): void {
        const userInputsForCorridorSearch: any = {};
        Object.assign(userInputsForCorridorSearch, pricingItem.userInputs, contractInfoInputs);

        if (pricingItem instanceof AbstractDeviceConsumablePricingItem) {
            userInputsForCorridorSearch['quantity'] = pricingItem.userInputs.quantity > 0 ? pricingItem.device.totalQuantity : 0;
            this.articleService.updateCorridor(pricingItem.device, userInputsForCorridorSearch, code, pricingItem.isCmc, salesType);
            userInputsForCorridorSearch['quantity'] = pricingItem.userInputs.consumableQuantity > 0 ? pricingItem.consumable.totalQuantity : 0;
            this.articleService.updateCorridor(pricingItem.consumable, userInputsForCorridorSearch, code, pricingItem.isCmc, salesType);
        } else if (pricingItem instanceof AbstractDevicePricingItem) {
            if (this.isWeeklyQuantity(pricingItem, code)) {
                userInputsForCorridorSearch['quantity'] = pricingItem.userInputs.quantity > 0 ? Math.ceil(pricingItem.device.totalQuantity * Number(WEEK_AVERAGE)) : 0;
            } else {
                userInputsForCorridorSearch['quantity'] = pricingItem.userInputs.quantity > 0 ? pricingItem.device.totalQuantity : 0;
            }
            this.articleService.updateCorridor(pricingItem.device, userInputsForCorridorSearch, code, pricingItem.isCmc, salesType);
        } else if (pricingItem instanceof ConsumablePricingItem) {
            userInputsForCorridorSearch['quantity'] = pricingItem.userInputs.consumableQuantity > 0 ? pricingItem.consumable.totalQuantity : 0;
            this.articleService.updateCorridor(pricingItem.consumable, userInputsForCorridorSearch, code, pricingItem.isCmc, salesType, pricingItem.consoType);
        } else if (pricingItem instanceof OptionPricingItem) {
            if (pricingItem.option) {
                this.articleService.updateCorridorFromSingleProductPricing(pricingItem.option);
            }
        }
    }

    private isWeeklyQuantity(pricingItem: AbstractDevicePricingItem, code: string): boolean {
        return (code === EnumQuoteServiceType.LS || code === EnumQuoteServiceType.VT)
            && (pricingItem.userInputs['billingMode'] && (pricingItem.userInputs['billingMode'].key === 'FM_ALP' || pricingItem.userInputs['billingMode'].key === 'FM_MAL'));
    }

    public sumOfAllArticlesQtyAndCoeffFreq(pricingItem: PricingItem, oldSumOfAllArticlesQtyAndCoeffFreq: Big, referential: any): Big {
        if (pricingItem instanceof AbstractDeviceConsumablePricingItem) {
            let total = oldSumOfAllArticlesQtyAndCoeffFreq.plus(parseBig(pricingItem.userInputs.quantity).mul(this.getMonthlyServiceByFrequency(pricingItem, referential)));
            total = total.plus(parseBig(pricingItem.userInputs.consumableQuantity).mul(this.getMonthlyServiceByFrequency(pricingItem, referential)));
            return total;
        }
        if (pricingItem instanceof AbstractDevicePricingItem) {
            return oldSumOfAllArticlesQtyAndCoeffFreq.plus(parseBig(pricingItem.userInputs?.quantity).mul(this.getMonthlyServiceByFrequency(pricingItem, referential)));
        }
        if (pricingItem instanceof ConsumablePricingItem) {
            return oldSumOfAllArticlesQtyAndCoeffFreq.plus(parseBig(pricingItem.userInputs.consumableQuantity).mul(this.getMonthlyServiceByFrequency(pricingItem, referential)));
        }
        return oldSumOfAllArticlesQtyAndCoeffFreq;
    }

    private getMonthlyServiceByFrequency(pricingItem: PricingItem, referential: any): Big {
        const frequency = pricingItem.userInputs.frequency.value;
        const index = referential.frequencies.findIndex((element: any) => element.frequency === frequency);
        if (index >= 0) {
            return parseBig(referential.frequencies[index].monthlyService.toString());
        }
        return new Big('0');
    }

    public applyLogisticsPriceToPricingItem(pricingItem: PricingItem, sumOfAllArticlesQtyAndCoeffFreq: Big, totalLogisticsPrice: string, referential: any): void {
        if (pricingItem.isCmc || !sumOfAllArticlesQtyAndCoeffFreq.gt(0)) {
            return;
        }
        if (pricingItem instanceof AbstractDeviceConsumablePricingItem) {
            const logisticsPriceImpact = this.getMonthlyServiceByFrequency(pricingItem, referential).div(sumOfAllArticlesQtyAndCoeffFreq).mul(new Big(totalLogisticsPrice));
            this.articleService.updateLastCorridorStateByAddingPrice(pricingItem.device, logisticsPriceImpact);
            this.articleService.updateLastCorridorStateByAddingPrice(pricingItem.consumable, logisticsPriceImpact);
        } else if (pricingItem instanceof AbstractDevicePricingItem) {
            const logisticsPriceImpact = this.getMonthlyServiceByFrequency(pricingItem, referential).div(sumOfAllArticlesQtyAndCoeffFreq).mul(totalLogisticsPrice);
            this.articleService.updateLastCorridorStateByAddingPrice(pricingItem.device, logisticsPriceImpact);
        } else if (pricingItem instanceof ConsumablePricingItem) {
            const logisticsPriceImpact = this.getMonthlyServiceByFrequency(pricingItem, referential).div(sumOfAllArticlesQtyAndCoeffFreq).mul(totalLogisticsPrice);
            this.articleService.updateLastCorridorStateByAddingPrice(pricingItem.consumable, logisticsPriceImpact);
        }
    }

    public applyFreeMonthFactor(pricingItem: PricingItem, factorFreeMonth: string): void {
        if (pricingItem.isCmc) {
            return;
        }
        const factorImpact: Big = parseBig(factorFreeMonth);
        if (pricingItem instanceof AbstractDeviceConsumablePricingItem) {
            this.articleService.applyFreeMonthFactor(pricingItem.device, factorImpact);
            this.articleService.applyFreeMonthFactor(pricingItem.consumable, factorImpact);
        } else if (pricingItem instanceof AbstractDevicePricingItem) {
            this.articleService.applyFreeMonthFactor(pricingItem.device, factorImpact);
        } else if (pricingItem instanceof ConsumablePricingItem) {
            this.articleService.applyFreeMonthFactor(pricingItem.consumable, factorImpact);
        }
    }

    public applyContractVtFactor(pricingItem: PricingItem, factorVt: string): void {
        if (pricingItem.isCmc) {
            return;
        }
        const factorImpact: Big = parseBig(factorVt);
        if (pricingItem instanceof AbstractDeviceConsumablePricingItem) {
            this.articleService.applyContractVtFactor(pricingItem.device, factorImpact);
            this.articleService.applyContractVtFactor(pricingItem.consumable, factorImpact);
        } else if (pricingItem instanceof AbstractDevicePricingItem) {
            this.articleService.applyContractVtFactor(pricingItem.device, factorImpact);
        } else if (pricingItem instanceof ConsumablePricingItem) {
            this.articleService.applyContractVtFactor(pricingItem.consumable, factorImpact);
        }
    }

    public applyServiceGlobalDiscountToPricingItem(pricingItem: PricingItem, distance: string, serviceColor: string): void {
        if (pricingItem instanceof AbstractDeviceConsumablePricingItem) {
            this.applyDiscountToArticle(pricingItem.device, distance, serviceColor);
            this.applyDiscountToArticle(pricingItem.consumable, distance, serviceColor);
            const totalAppliedPrice = this.calculateTotalUnitAppliedPrice(pricingItem);
            if (totalAppliedPrice.gt(0)) {
                const totalCorridor = this.getTotalCorridor(pricingItem);
                const totalAppliedPriceColor = this.corridorService.getMatchedColorByappliedPrice(totalCorridor, bigToString(totalAppliedPrice), 'LIGHTGREEN');
                this.updateDeviceConsumable(pricingItem, totalAppliedPrice, totalCorridor, totalAppliedPriceColor);
            }
        } else if (pricingItem instanceof AbstractDevicePricingItem) {
            const corridorLevel = pricingItem.device.corridor ? this.corridorService.getMatchedLevelByColor(pricingItem.device.corridor, serviceColor) : undefined;
            this.articleService.applyServiceGlobalDiscountToArticle(pricingItem.device, distance, corridorLevel, serviceColor);
        } else if (pricingItem instanceof ConsumablePricingItem) {
            const corridorLevel = pricingItem.consumable?.corridor ? this.corridorService.getMatchedLevelByColor(pricingItem.consumable.corridor, serviceColor) : undefined;
            this.articleService.applyServiceGlobalDiscountToArticle(pricingItem.consumable, distance, corridorLevel, serviceColor);
        } else if (pricingItem instanceof OptionPricingItem) {
            const corridorLevel = pricingItem.option?.corridor ? this.corridorService.getMatchedLevelByColor(pricingItem.option.corridor, serviceColor) : undefined;
            this.articleService.applyServiceGlobalDiscountToArticle(pricingItem.option, distance, corridorLevel, serviceColor);
        }
    }

    private applyDiscountToArticle(article: Article, distance: string, serviceColor: string): void {
        if (!this.articleService.isFlat(article)) {
            const corridorLevel = article?.corridor ? this.corridorService.getMatchedLevelByColor(article.corridor, serviceColor) : undefined;
            this.articleService.applyServiceGlobalDiscountToArticle(article, distance, corridorLevel, serviceColor);
        }
    }

    private getTotalCorridor(pricingItem: AbstractDeviceConsumablePricingItem): Corridor {
        const totalCorridor = new Corridor();
        if (pricingItem.device.corridor) {
            this.corridorService.addCorridor(totalCorridor, pricingItem.device.corridor);
        }
        if (pricingItem.consumable.corridor) {
            this.corridorService.addCorridor(totalCorridor, pricingItem.consumable.corridor);
        }
        return totalCorridor;
    }

    private calculateTotalUnitAppliedPrice(pricingItem: AbstractDeviceConsumablePricingItem): Big {
        let total = new Big(0);
        if (pricingItem.device.appliedPrice) {
            total = total.add(new Big(pricingItem.device.appliedPrice));
        }
        if (pricingItem.consumable.appliedPrice) {
            total = total.add(new Big(pricingItem.consumable.appliedPrice));
        }
        return total;
    }

    private updateDeviceConsumable(pricingItem: AbstractDeviceConsumablePricingItem, totalAppliedPrice: Big, totalCorridor: Corridor, totalAppliedPriceColor: string): void {
        if (pricingItem instanceof DeviceConsumableWithSuppPricingItem) {
            Object.assign(pricingItem, {
                discount: bigToString(Tools.getDiscount(totalAppliedPrice, parseBig(this.corridorService.getHighestLevel(totalCorridor)?.min))),
                totalAppliedPriceColor,
                deviceConsumableCorridor: totalCorridor,
                deviceConsumableAppliedPrice: bigToString(totalAppliedPrice)
            });
        } else if (pricingItem instanceof DeviceConsumablePricingItem) {
            Object.assign(pricingItem, {
                discount: bigToString(Tools.getDiscount(totalAppliedPrice, parseBig(this.corridorService.getHighestLevel(totalCorridor)?.min))),
                totalAppliedPriceColor
            });
        }
    }

    public getCorridorDevices(pricingItem: PricingItem, isWithQty: boolean, isWithNonNegotiableArticle: boolean): Corridor {
        if (pricingItem instanceof PackPricingItem || pricingItem instanceof DeviceConsumablePricingItem || pricingItem instanceof DeviceConsumableWithSuppPricingItem || pricingItem instanceof DevicePricingItem) {
            return this.articleService.getCorridor(pricingItem.device, isWithQty, isWithNonNegotiableArticle, pricingItem.userInputs.quantity);
        }
        return new Corridor();
    }

    public getCorridorOptions(pricingItem: PricingItem, isWithQty: boolean, isWithNonNegotiableArticle: boolean): Corridor {
        if (pricingItem instanceof OptionPricingItem) {
            return this.articleService.getCorridor(pricingItem.option, isWithQty, isWithNonNegotiableArticle, pricingItem.userInputs.quantity);
        }
        return new Corridor();
    }

    public getCorridorConsumables(pricingItem: PricingItem, isWithQty: boolean, isWithNonNegotiableArticle: boolean): Corridor {
        if (pricingItem instanceof ConsumablePricingItem) {
            return this.articleService.getCorridor(pricingItem.consumable, isWithQty, isWithNonNegotiableArticle, pricingItem.userInputs.consumableQuantity);
        }
        if (pricingItem instanceof DeviceConsumablePricingItem || pricingItem instanceof DeviceConsumableWithSuppPricingItem) {
            return this.articleService.getCorridor(pricingItem.consumable, isWithQty, isWithNonNegotiableArticle, pricingItem.userInputs.consumableQuantity);
        }
        return new Corridor();
    }

    public applyWaterDeliveryFactorToPricingItem(pricingItem: PricingItem, waterDeliveryFactor: Big, isWaterBottleDeliveryWasChanged: boolean): void {
        if (pricingItem.isCmc) {
            return;
        }
        if (pricingItem instanceof AbstractDeviceConsumablePricingItem) {
            if (pricingItem.device) this.articleService.updateLastCorridorStateBymultiplyingWaterDeliveryFactor(pricingItem.device, waterDeliveryFactor, isWaterBottleDeliveryWasChanged);
            if (pricingItem.consumable) this.articleService.updateLastCorridorStateBymultiplyingWaterDeliveryFactor(pricingItem.consumable, waterDeliveryFactor, isWaterBottleDeliveryWasChanged);
        } else if (pricingItem instanceof AbstractDevicePricingItem) {
            if (pricingItem.device) this.articleService.updateLastCorridorStateBymultiplyingWaterDeliveryFactor(pricingItem.device, waterDeliveryFactor, isWaterBottleDeliveryWasChanged);
        } else if (pricingItem instanceof ConsumablePricingItem) {
            if (pricingItem.consumable) this.articleService.updateLastCorridorStateBymultiplyingWaterDeliveryFactor(pricingItem.consumable, waterDeliveryFactor, isWaterBottleDeliveryWasChanged);
        } else if (pricingItem instanceof OptionPricingItem) {
            if (pricingItem.option) this.articleService.updateLastCorridorStateBymultiplyingWaterDeliveryFactor(pricingItem.option, waterDeliveryFactor, isWaterBottleDeliveryWasChanged);
        }
    }

    public setArticleCorridorFromArticleLastCorridorState(pricingItem: PricingItem): void {
        if (pricingItem instanceof AbstractDeviceConsumablePricingItem) {
            this.articleService.setArticleCorridorFromArticleLastCorridorState(pricingItem.consumable);
            this.articleService.setArticleCorridorFromArticleLastCorridorState(pricingItem.device);
        } else if (pricingItem instanceof AbstractDevicePricingItem) {
            this.articleService.setArticleCorridorFromArticleLastCorridorState(pricingItem.device);
        } else if (pricingItem instanceof ConsumablePricingItem) {
            this.articleService.setArticleCorridorFromArticleLastCorridorState(pricingItem.consumable);
        } else if (pricingItem instanceof OptionPricingItem) {
            this.articleService.setArticleCorridorFromArticleLastCorridorState(pricingItem.option);
        }
    }

    public copyArticleAppliedPriceToOldAppliedPrice(pricingItem: PricingItem): void {
        if (pricingItem instanceof AbstractDeviceConsumablePricingItem) {
            this.articleService.copyArticleAppliedPriceToOldAppliedPrice(pricingItem.consumable);
            this.articleService.copyArticleAppliedPriceToOldAppliedPrice(pricingItem.device);
        } else if (pricingItem instanceof AbstractDevicePricingItem) {
            this.articleService.copyArticleAppliedPriceToOldAppliedPrice(pricingItem.device);
        } else if (pricingItem instanceof ConsumablePricingItem) {
            this.articleService.copyArticleAppliedPriceToOldAppliedPrice(pricingItem.consumable);
        } else if (pricingItem instanceof OptionPricingItem) {
            this.articleService.copyArticleAppliedPriceToOldAppliedPrice(pricingItem.option);
        }
    }

    public calculateArticleDistanceAndColorAndPriceFixedCorridorMode(pricingItem: PricingItem, nonNegociableArticleColor: string): void {
        if (pricingItem instanceof AbstractDeviceConsumablePricingItem) {
            this.articleService.calculateArticleDistanceAndColorAndPriceFixedCorridorMode(pricingItem.device, nonNegociableArticleColor);
            this.articleService.calculateArticleDistanceAndColorAndPriceFixedCorridorMode(pricingItem.consumable, nonNegociableArticleColor);
        } else if (pricingItem instanceof AbstractDevicePricingItem) {
            this.articleService.calculateArticleDistanceAndColorAndPriceFixedCorridorMode(pricingItem.device, nonNegociableArticleColor);
        } else if (pricingItem instanceof ConsumablePricingItem) {
            this.articleService.calculateArticleDistanceAndColorAndPriceFixedCorridorMode(pricingItem.consumable, nonNegociableArticleColor);
        } else if (pricingItem instanceof OptionPricingItem) {
            this.articleService.calculateArticleDistanceAndColorAndPriceFixedCorridorMode(pricingItem.option, nonNegociableArticleColor);
        }
    }

    public calculateArticleDistanceAndColorAndPriceFixedPriceMode(pricingItem: PricingItem, nonNegociableArticleColor: string): void {
        if (pricingItem instanceof AbstractDeviceConsumablePricingItem) {
            this.articleService.calculateArticleDistanceAndColorAndPriceFixedPriceMode(pricingItem.device, nonNegociableArticleColor);
            this.articleService.calculateArticleDistanceAndColorAndPriceFixedPriceMode(pricingItem.consumable, nonNegociableArticleColor);
        } else if (pricingItem instanceof AbstractDevicePricingItem) {
            this.articleService.calculateArticleDistanceAndColorAndPriceFixedPriceMode(pricingItem.device, nonNegociableArticleColor);
        } else if (pricingItem instanceof ConsumablePricingItem) {
            this.articleService.calculateArticleDistanceAndColorAndPriceFixedPriceMode(pricingItem.consumable, nonNegociableArticleColor);
        } else if (pricingItem instanceof OptionPricingItem) {
            this.articleService.calculateArticleDistanceAndColorAndPriceFixedPriceMode(pricingItem.option, nonNegociableArticleColor);
        }
    }

    public applyMepPriceToPricingItem(pricingItem: PricingItem, contractDuration: Big, referential: any): void {
        if (!(pricingItem instanceof PackPricingItem || pricingItem instanceof DeviceConsumablePricingItem || pricingItem instanceof DeviceConsumableWithSuppPricingItem || pricingItem instanceof DevicePricingItem)) {
            return;
        }
        if (pricingItem.isCmc) {
            return;
        }
        const device = pricingItem.device;
        const mepCostDividend = this.articleService.calculateUnitaryPrice(device, pricingItem.userInputs.frequency, referential)
            .add(this.articleService.calculateUnitaryMepPrice(device, contractDuration, referential));
        const mepCost = pricingItem.userInputs && !MathUtils.bigIsNullOrZero(parseBig(pricingItem.userInputs.quantity)) ?
            mepCostDividend.div(device.totalQuantity) : mepCostDividend;
        this.articleService.updateLastCorridorStateByAddingPrice(device, mepCost);
    }
}
